import type { OHLCVPoint } from '../../api/types';

// ─── What: volume panel bucketing ─────────────────────────────────────────────
//
// The panel stacks volume two different ways depending on what is on screen.
//
// Both shapes report each market's TRUE taker volume — YES plus NO. The sides
// do not double-count: cagg_market_1m aggregates taker fills only
// (is_maker = false), so a YES row and a NO row are two distinct actions.
//
// With several markets drawn — an event's candidates — each market contributes
// one segment holding its own two sides summed. Volume is NEVER summed ACROSS
// markets into a single figure: "all candidates" is not a quantity anyone
// trades, and the per-market segments are what keeps that visible.
//
// With a single market drawn there is no per-market breakdown left to show, so
// the stack splits by side instead: NO at the bottom, YES above. The bar's
// height is the same total either way.
//
// `vol` is volume_quote — already USD. It is never multiplied by price.

export interface VolumeSegment {
    /** Stack identity: a conditionId (multi-market) or 'yes' | 'no'. */
    key: string;
    /** Market this segment belongs to, for click-through and colouring. */
    conditionId: string;
    /** Which side, when the stack is split by side; null when split by market. */
    side: 'yes' | 'no' | null;
    vol: number;
}

export interface VolumeBucket {
    bucketMs: number;
    /** Draw order, bottom of the stack first. */
    segments: VolumeSegment[];
    total: number;
}

/**
 * The lone market whose two sides should be split apart, or null.
 *
 * Splitting is only meaningful when there is no per-market breakdown competing
 * for the same stack — with several markets drawn, sides would double the
 * segment count and bury the comparison the panel exists to show.
 */
function splitSideFor(
    series: { conditionId: string; data: OHLCVPoint[] }[],
    noByMarket: ReadonlyMap<string, OHLCVPoint[]>
): string | null {
    const withData = series.filter(s => s.data.length > 0);
    if (withData.length !== 1) return null;
    const only = withData[0].conditionId;
    return (noByMarket.get(only)?.length ?? 0) > 0 ? only : null;
}

export function buildVolumeBuckets(
    series: { conditionId: string; data: OHLCVPoint[] }[],
    noByMarket: ReadonlyMap<string, OHLCVPoint[]>,
    xDomain: [Date, Date],
    resolutionMin: number
): VolumeBucket[] {
    const resMs = resolutionMin * 60 * 1000;
    const fromMs = xDomain[0].getTime();
    const toMs = xDomain[1].getTime();
    const splitMarket = splitSideFor(series, noByMarket);
    const split = splitMarket !== null;

    // bucketMs -> segment key -> segment. Keyed rather than pushed so repeated
    // points inside one bucket merge instead of stacking on themselves.
    const buckets = new Map<number, Map<string, VolumeSegment>>();

    const add = (
        t: Date,
        vol: number,
        key: string,
        conditionId: string,
        side: 'yes' | 'no' | null
    ) => {
        const ms = t.getTime();
        if (ms < fromMs || ms > toMs) return;
        const bucketMs = Math.floor(ms / resMs) * resMs;
        let bucket = buckets.get(bucketMs);
        if (!bucket) {
            bucket = new Map();
            buckets.set(bucketMs, bucket);
        }
        const existing = bucket.get(key);
        if (existing) existing.vol += vol;
        else bucket.set(key, { key, conditionId, side, vol });
    };

    // In the split branch every point collapses under the key 'yes', which is
    // only sound because splitSideFor() found exactly one market with data.
    // Narrow the input to that market so the branch cannot see a second one even
    // if that guarantee were ever weakened.
    const yesSeries = split
        ? series.filter(s => s.conditionId === splitMarket)
        : series;
    for (const s of yesSeries) {
        for (const d of s.data) {
            add(
                d.t,
                d.vol,
                split ? 'yes' : s.conditionId,
                s.conditionId,
                split ? 'yes' : null
            );
        }
    }

    // The NO side. Split apart for a lone market; folded into that market's own
    // segment otherwise, so each candidate's bar is its true taker volume rather
    // than only the half of it that traded YES.
    for (const s of yesSeries) {
        const noPoints = noByMarket.get(s.conditionId);
        if (!noPoints) continue;
        for (const d of noPoints) {
            add(
                d.t,
                d.vol,
                split ? 'no' : s.conditionId,
                s.conditionId,
                split ? 'no' : null
            );
        }
    }

    // NO first so it draws at the bottom of the stack; market segments keep the
    // order their series arrived in.
    const order = (seg: VolumeSegment) => (seg.side === 'no' ? 0 : 1);

    return [...buckets.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([bucketMs, segMap]) => {
            const segments = [...segMap.values()].sort(
                (a, b) => order(a) - order(b)
            );
            return {
                bucketMs,
                segments,
                total: segments.reduce((sum, seg) => sum + seg.vol, 0),
            };
        });
}
